import { Event, useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { Icon, Button, styles, Theme, useDevice, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Calendar, Places } from './components';
import { Compact } from './Finder.compact';
import { FIELD, PLACES_LENGTH_LIMIT, TYPE, VIEW } from './Finder.constants';
import { L10N } from './Finder.l10n';
import * as style from './Finder.module.css';
import {
  getDataSource,
  getFieldPosition,
  getForwarderUrl,
  getHotels,
  getOccupationDataSource,
  getPlaceCaption,
  parseCalendarProps,
  parseValue,
  setStorage,
} from './helpers';
import { getAvailableWidth } from './helpers/getAvailableWidth';
import { fetchCurrency, fetchUrlParams } from '../../helpers';
// ! TODO: Why we don't publish prepareRoom in ../../helpers
import { prepareRoom } from '../../helpers/prepareRoom';
import { getSafeTrust } from '../../trust';
import { Field, FieldOccupation, FieldPromocode, Skeleton } from '../__shared__';
import { DATASOURCE } from '../__shared__/Occupation/Occupation.constants';
import { EVENT, getCalendarCaption, ICON } from '../helpers';

const { CHAIN, HOTEL } = TYPE;

const Finder = ({
  compacted = false,
  icon = false,
  layout,
  storage = true,
  text,
  onChange = () => {},
  onSubmit,
  ...others
}) => {
  const { isMobile, width: deviceWidth } = useDevice();
  const { dateFormat, translate } = useLocale();
  const ref = useRef(null);
  const placesRef = useRef(null);
  const {
    set,
    value: {
      components = {},
      currency: propCurrency,
      finder: storeFinder,
      forwarder,
      locale,
      promo = {},
      skeleton = false,
      type = HOTEL,
      ...store
    },
  } = useStore();

  const [currency, setCurrency] = useState();
  const [error, setError] = useState({});
  const [focus, setFocus] = useState();
  const [unavailability, setUnavailability] = useState();
  const [value, setValue] = useState(parseValue(storeFinder));
  const [visited, setVisited] = useState({});
  const [warning, setWarning] = useState();
  const [width, setWidth] = useState();

  const trust = getSafeTrust(store);

  useEffect(() => {
    if (!skeleton) trust.record('stageEntered', { stage: 'finder' });
  }, [skeleton, trust]);

  useEffect(() => {
    if (skeleton) return;

    if (!storeFinder?.occupation) set({ finder: { ...storeFinder, occupation: [prepareRoom(DATASOURCE)] } });

    if (is.debug && !storeFinder?.occupation) {
      // eslint-disable-next-line no-console
      console.log({ finder: { ...storeFinder, occupation: [prepareRoom(DATASOURCE)] } });
    }
    window.addEventListener('pageshow', handlePageShow);
    !isMobile && document.addEventListener('click', handleClickOutside, true);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      !isMobile && document.removeEventListener('click', handleClickOutside, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { current: el } = ref || {};
    if (!is.column || !el) return;

    const observer = new IntersectionObserver(
      ([{ isIntersecting } = {}] = []) => isIntersecting && setWidth(getAvailableWidth(el)),
      { threshold: 0.1 },
    );
    if (el) observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceWidth]);

  useEffect(() => {
    unavailability && setUnavailability({ disabledDates: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.occupation, value?.place]);

  useEffect(() => {
    const { place: { id: [hotelId] = [], isHotel } = {} } = value || {};
    const nextPromocode = !is.rates && ((promo.variants && promo.variants[hotelId]) || promo.code);
    if (nextPromocode) {
      const next = { ...value, promocode: nextPromocode };
      setValue(next);
      publishChange(next);
    }
    if (isHotel && !fetchUrlParams(window.location.search)?.currency) {
      (async () => setCurrency(await fetchCurrency(hotelId)))();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.place]);

  useEffect(() => {
    const handleFinder = ({ calendar, finder, focus } = {}) => {
      if (finder) return handleSubmit({ nextValue: finder });

      setUnavailability(calendar);
      if (focus) setFocus(focus);
    };
    Event.subscribe(EVENT.FINDER, handleFinder);

    return () => Event.unsubscribe(EVENT.FINDER, handleFinder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, propCurrency]);

  // ! TODO: Why this one? Is not responsibility of <Finder> change the currency when prop changes.
  useEffect(() => {
    setCurrency(id ? propCurrency : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propCurrency]);

  useEffect(
    () => {
      const next = parseValue({ ...value, ...storeFinder });
      if (JSON.stringify(value) !== JSON.stringify(next)) {
        setValue(next);
        publishChange(next);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storeFinder],
  );

  useEffect(() => {
    focus && setVisited({ ...visited, [`${focus}`]: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  const handleChange = (field, fieldValue, event) => {
    getSafeTrust(store).record('journeyAction', {
      stage: 'finder',
      name: `${field}Changed`,
      trusted: event?.isTrusted,
    });
    const next = { ...value, [field]: fieldValue };
    getSafeTrust(store).record('finderChanged');

    setValue(next);
    onChange(next, field, fieldValue);
    publishChange(next);
  };

  const handleClickOutside = ({ target } = {}) => !ref.current?.contains(target) && setFocus(undefined);

  const handlePageShow = ({ persisted } = {}) => {
    if (!persisted) return;

    setFocus();
    setVisited();

    const next = parseValue(storeFinder);
    setValue(next);
    publishChange(next);
  };

  const handlePlaceChange = (place) => {
    setWarning(false);
    handleChange(VIEW.PLACE, place);
    !is.rates && assistant();
  };

  const handleSubmit = ({ nextValue = value } = {}) => {
    if (dataSource.places && value.place === undefined) return setFocus(VIEW.PLACE);

    if (value.calendar?.length !== 2) return setFocus(VIEW.CALENDAR);

    const [firstError] = Object.keys(error).filter((key) => !!error[key]);
    if (firstError) return setFocus(firstError);

    setWarning(false);
    setFocus(undefined);
    if (!nextValue?.occupation) {
      nextValue = { ...nextValue, occupation: [prepareRoom(DATASOURCE)] };
    }
    set({ finder: nextValue });
    const forwarderUrl = getForwarderUrl({ ...nextValue, currency, forwarder, locale, store, type });

    storage && setStorage(nextValue, store?.id);
    getSafeTrust(store).record('searchSubmitted', {
      ...store,
      finder: nextValue,
      trustRooms: nextValue.occupation.length,
    });
    if (onSubmit) onSubmit({ ...nextValue, forwarderUrl });
    else window.location.href = forwarderUrl;
  };

  const assistant = () => {
    const { calendar = [] } = value;
    if (focus === VIEW.PLACE && !visited.calendar && calendar.length !== 2) setFocus(VIEW.CALENDAR);
    else if (!compacted || [VIEW.CALENDAR, VIEW.PLACE].includes(focus)) setFocus(undefined);
  };

  const publishChange = (next) =>
    Object.keys(next || []).forEach((field) => {
      if (JSON.stringify(value[field]) !== JSON.stringify(next[field])) {
        Event.publish(EVENT.FINDER_FIELD_CHANGE, { field, previousValue: value[field], value: next[field] });
      }
    });

  const { calendar = [], occupation, place = {}, promocode } = value;
  const { id: ids = [], isHotel } = place;
  const dataSource = getDataSource({ type, ...store });
  const { hotel: { id: idHotel } = {} } = store;
  const id = type !== HOTEL ? (isHotel && ids.length === 1 ? ids[0] : undefined) : idHotel;

  const { separatorColor = '', ...fieldVariant } = Theme.get('--mirai-finder-field-');

  const is = {
    inline: separatorColor !== 'transparent',
    column: layout === 'column',
    expand: !isMobile && layout === 'expand',
    compacted: layout !== 'column' && (isMobile || (compacted && !focus)),
    debug: store?.id === '500138',
    rates: components.rates !== undefined,
  };

  const fieldProps = {
    compacted: is.compacted,
    hasPlaces: !!dataSource.places,
    inline: is.inline,
    ...(!is.compacted ? getFieldPosition(ref, placesRef) : undefined),
    text: is.rates ? translate(L10N.ACTION_SEARCH) : translate(L10N.ACTION_APPLY),
    variant: Object.keys(fieldVariant).length ? fieldVariant : undefined,
    className: style.field,
    style: is.column && width ? { maxWidth: width, width } : undefined,
  };

  // eslint-disable-next-line no-console
  is.debug && console.log({ place, calendar, occupation });

  return (
    <>
      {is.compacted && (
        <Compact
          {...{ dataSource, skeleton, value }}
          onExpand={(field) => {
            trust.record('journeyAction', { stage: 'finder', name: `${field}Opened` });
            setFocus(field);
          }}
          onSubmit={!is.rates || (is.rates && !isMobile) ? handleSubmit : undefined}
          {...others}
        />
      )}

      <View
        ref={ref}
        role="finder"
        data-trust-stage="finder"
        data-trust-scope={getSafeTrust(store).scope}
        row={!is.column}
        tag="finder"
        testId={others.testId}
        className={
          !is.compacted
            ? styles(style.finder, is.column && style.column, is.expand && style.expand, others.className)
            : undefined
        }
      >
        {(dataSource.places || (skeleton && type === CHAIN)) && (
          <>
            <Field
              {...FIELD.PLACE}
              caption={getPlaceCaption(place.value, dataSource.places?.options)}
              label={translate(L10N.LABEL_WHERE)}
              expanded={dataSource?.places?.options?.length >= PLACES_LENGTH_LIMIT}
              markdown={false}
              placeholder={dataSource?.places?.placeholder || translate(L10N.LABEL_WHERE_PLACEHOLDER)}
              visible={focus === VIEW.PLACE}
              onPress={(event) => {
                if (focus !== VIEW.PLACE)
                  getSafeTrust(store).record('journeyAction', {
                    stage: 'finder',
                    name: 'placeOpened',
                    trusted: event?.isTrusted,
                  });
                setFocus(focus !== VIEW.PLACE ? VIEW.PLACE : undefined);
              }}
              {...fieldProps}
            >
              <Places
                autoColumn={focus === VIEW.PLACE}
                dataSource={dataSource?.places?.options}
                text={compacted ? text : undefined}
                ref={placesRef}
                value={place.value}
                onChange={handlePlaceChange}
                onSubmit={is.rates ? handleSubmit : undefined}
                testId={others.testId ? `${others.testId}-places` : undefined}
              />
            </Field>

            {is.inline && <View className={style.separator} />}
          </>
        )}

        <Field
          {...FIELD.CALENDAR}
          caption={calendar.length ? getCalendarCaption(calendar, translate, dateFormat) : undefined}
          centered={type !== HOTEL && !is.column}
          label={translate(L10N.LABEL_WHEN)}
          placeholder={getCalendarCaption(undefined, translate, dateFormat)}
          visible={focus === VIEW.CALENDAR}
          onPress={(event) => {
            if (focus !== VIEW.CALENDAR)
              getSafeTrust(store).record('journeyAction', {
                stage: 'finder',
                name: 'calendarOpened',
                trusted: event?.isTrusted,
              });
            setFocus(focus !== VIEW.CALENDAR ? VIEW.CALENDAR : undefined);
          }}
          testId={others.testId ? `${others.testId}-calendar-field` : undefined}
          {...fieldProps}
        >
          <Calendar
            {...parseCalendarProps(dataSource.calendar, id, occupation, type, unavailability, dataSource.variants)}
            id={id}
            currency={id ? currency : undefined}
            locale={locale}
            rangeMinDays={1}
            text={compacted ? text : undefined}
            top={fieldProps?.top}
            skeleton={skeleton}
            unavailability={unavailability}
            value={calendar}
            onChange={(nextCalendar, event) => {
              handleChange(VIEW.CALENDAR, nextCalendar, event);
              if (nextCalendar.length === 2 && !is.rates) {
                setError({ ...error, calendar: undefined });
                assistant();
              }
            }}
            onError={(calendar) => setError({ ...error, calendar })}
            onSubmit={is.rates ? handleSubmit : undefined}
            onValid={() => setError({ ...error, calendar: undefined })}
            testId={others.testId ? `${others.testId}-calendar` : undefined}
          />
        </Field>

        {is.inline && <View className={style.separator} />}

        <FieldOccupation
          dataSource={getOccupationDataSource({
            ...dataSource,
            places: dataSource.places?.options,
            place: place?.value,
          })}
          hotels={getHotels(dataSource.placesSource, place.value)}
          maxRooms={dataSource.maxRooms}
          text={compacted ? text : undefined}
          value={occupation}
          visible={focus === VIEW.OCCUPATION}
          withWarning={warning}
          onChange={(occupation, event) => handleChange(VIEW.OCCUPATION, occupation, event)}
          onError={(occupation) => {
            if (!isMobile && focus !== VIEW.OCCUPATION) setFocus(VIEW.OCCUPATION);
            setError({ ...error, occupation });
          }}
          onPress={(event) => {
            if (focus !== VIEW.OCCUPATION)
              getSafeTrust(store).record('journeyAction', {
                stage: 'finder',
                name: 'occupationOpened',
                trusted: event?.isTrusted,
              });
            setFocus(focus !== VIEW.OCCUPATION ? VIEW.OCCUPATION : undefined);
          }}
          onSubmit={is.rates ? handleSubmit : isMobile && is.compacted ? () => setFocus() : undefined}
          onValid={() => setError({ ...error, occupation: undefined })}
          onWarning={() => {
            if (focus !== VIEW.OCCUPATION) setFocus(VIEW.OCCUPATION);
          }}
          testId={others.testId ? `${others.testId}-occupation` : undefined}
          {...fieldProps}
        />

        {is.inline && <View className={style.separator} />}

        <FieldPromocode
          value={promocode}
          visible={focus === VIEW.PROMOCODE}
          onChange={(promocode, event) => handleChange(VIEW.PROMOCODE, promocode, event)}
          onEnter={!is.compacted ? () => setFocus(VIEW.PROMOCODE) : undefined}
          onLeave={!is.compacted ? () => setFocus(undefined) : undefined}
          onPress={
            isMobile && is.compacted
              ? (event) => {
                  if (focus !== VIEW.PROMOCODE)
                    trust.record('journeyAction', {
                      stage: 'finder',
                      name: 'promocodeOpened',
                      trusted: event?.isTrusted,
                    });
                  setFocus(focus !== VIEW.PROMOCODE ? VIEW.PROMOCODE : undefined);
                }
              : undefined
          }
          onSubmit={isMobile && is.compacted ? (is.rates ? handleSubmit : () => setFocus()) : undefined}
          testId={others.testId ? `${others.testId}-promocode` : undefined}
          {...fieldProps}
        />

        {!is.compacted ? (
          skeleton ? (
            <Skeleton large button words={1} />
          ) : (
            <Button
              disabled={skeleton}
              large
              role="finder-button"
              wide={is.column}
              onPress={handleSubmit}
              className={styles(style.button, icon && !is.column && style.withIcon)}
              testId={others.testId ? `${others.testId}-submit` : undefined}
            >
              {icon && !is.column ? <Icon value={ICON.SEARCH} /> : text || translate(L10N.ACTION_SEARCH)}
            </Button>
          )
        ) : null}
      </View>
    </>
  );
};

Finder.displayName = 'Mirai:Core:Finder';

Finder.propTypes = {
  compacted: PropTypes.bool,
  icon: PropTypes.bool,
  layout: PropTypes.oneOf(['column', 'expand']),
  storage: PropTypes.bool,
  text: PropTypes.string,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
};

export { Finder };
